import { IonApp, IonSpinner, setupIonicReact } from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { useCallback } from "react";
import { Redirect, type RouteComponentProps } from "react-router-dom";
import { AppUrlListener } from "@/components/global/app-url-listener";
import { ModalProvider } from "@/components/modal/modal-provider";
import { Toaster } from "@/components/ui/sonner";
import { PostHogPageView } from "@/lib/providers/posthog/components/PageView";
import { Routes } from "@/lib/router";
import { history } from "@/lib/router/history";
import { NotificationListener } from "./components/global/notification-listener";
import { useAuthType } from "./lib/hooks/use-auth-type";
import { GlobalRouter } from "./routes/routers/global.router";

setupIonicReact({ mode: "ios" });

export function App() {
  const { isLoading, isSignedIn, isEmployee, isAdmin } = useAuthType();

  const isUnauthenticated = !isSignedIn || (!isEmployee && !isAdmin);
  const isAuthenticated = !isUnauthenticated;

  const requireAuth = useCallback(
    (
      component: <T extends object>(props: RouteComponentProps<T>) => JSX.Element,
      options?: { required?: boolean; authPath?: string; unauthPath?: string },
    ) =>
      // eslint-disable-next-line react/display-name
      <T extends object>(props: RouteComponentProps<T>) => {
        const isRequired = options?.required ?? true;

        // if (isLoading) {
        //   return (
        //     <div className="flex h-full w-full items-center justify-center">
        //       <IonSpinner />
        //     </div>
        //   );
        // }

        if (isRequired && !isAuthenticated) {
          const currentPath = window.location.pathname;

          if (currentPath === Routes.AuthSignInPassword.pattern) {
            return <></>;
          }

          const signInUrl =
            options?.unauthPath ??
            Routes.AuthSignInPassword(undefined, {
              search: { callbackUrl: currentPath },
            });

          return <Redirect to={signInUrl} />;
        }

        if (!isRequired && isAuthenticated) {
          return <Redirect to={options?.authPath ?? Routes.Projects()} />;
        }

        return component(props);
      },
    [isAuthenticated, isLoading],
  );

  if (isLoading) {
    return (
      <IonApp>
        <div className="flex h-full w-full items-center justify-center">
          <IonSpinner />
        </div>
      </IonApp>
    );
  }

  return (
    <IonApp>
      <IonReactRouter history={history}>
        <ModalProvider />
        {!isLoading && <AppUrlListener />}
        <NotificationListener />
        <PostHogPageView />
        <Toaster />

        <GlobalRouter
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          requireAuth={requireAuth}
        />
      </IonReactRouter>
    </IonApp>
  );
}
